import dayjs from "dayjs";
import { EventEmitter } from "@angular/core";
import { IStream, Metadata } from "@auvious/rtc";
import {
  AssetTypeEnum,
  DateRangePresetEnum,
  MetricRangeEnum,
  MetricTypeEnum,
  StreamTrackKindEnum,
  TileTypeEnum,
  TranscriptIntentType,
  TranscriptStateEnum,
} from "../core-ui.enums";
import { IArea } from "./IPoint";
import {
  UserRoleEnum,
  AuditActionEnum,
  AuditApplicationEnum,
  AuditOutcomeEnum,
  LayoutEnum,
} from "../core-ui.enums";


export interface ISnapshotMetadata {
  order?: number;
  thumbSize?: IArea;
  zoomLevel?: number;
  rotationDegrees?: number;
  height?: number;
  left?: number;
  top?: number;
  width?: number;
  naturalWidth?: number;
  naturalHeight?: number;
}

export interface ITile {
  type: TileTypeEnum;
  ratio: number;
  container: HTMLDivElement;
  uuid?: string;
  isPortraitMode?: boolean;
  isOrientationPortrait?: boolean;
  isSpotlight?: boolean;

  setLayoutType: (type: LayoutEnum) => void;

  ready: EventEmitter<ITile>;
  destroyed: EventEmitter<ITile>;
}

export interface IVolume {
  level: number;
  streamId: string;
  speaking: boolean;
}

export interface ITag {
  id?: string;
  label: string;
  value: string;
  color?: string;
  tooltip?: string;
}

export interface ITrackMetadata {
  id?: string;
  settings?: MediaTrackSettings;
  capabilities?: MediaTrackCapabilities;
  constraints?: MediaTrackConstraints;
  /** whether noise suppression is currently active on this (audio) track */
  noiseSuppression?: boolean;
}

export interface IStreamMetadata extends Metadata {
  video?: ITrackMetadata;
  audio?: ITrackMetadata;
  portraitMode?: boolean;
  publishedAt?: string;
  correlationId?: string; // id that is created before the stream is published so that we can correlate the published stream with the local one
  primary?: boolean; // if this is the primary stream of the user (has audio and video) or a secondary (2nd camera)
  participantName?: string; // needed for transcript
  participantRoles?: string; // needed for transcript
  cobrowse?: boolean; // a screen the customer shares for co-browse, shown only in the co-browse session
}

export interface IEndpointMetadataChangedEvent {
  userId: string;
  userEndpointId: string;
  newMetadata: Metadata;
}

export interface IStreamMetadataChangedEvent
  extends IEndpointMetadataChangedEvent {
  streamId: string;
  newMetadata: IStreamMetadata;
}

export interface IStreamMuteChangeEvent {
  stream: IStream;
  muted: boolean;
  trackKind: StreamTrackKindEnum;
}

export interface IStreamMuteWillChangeEvent {
  stream: IStream;
  mute: boolean;
  trackKind: StreamTrackKindEnum;
}

export interface IPendingStreamOptions {
  id?: string;
  force?: boolean;
  kind?: StreamTrackKindEnum;
}

export interface ITranscript {
  chatTranscriptList: ITranscriptMessage[];
  conversationId: string;
  instanceId: string;
  recorderId: string;
}

export interface ITranscriptMessage {
  transcript: string;
}

export interface IOpenAITranscriptMessage extends ITranscriptMessage {
  endedAt: string;
  name: string;
  roles: string[];
  startedAt: string;
}

export interface IOpenAITranslationMessage extends IOpenAITranscriptMessage {
  translation: string;
}

export interface IGoogleTranscriptMessage extends ITranscriptMessage {
  id?: string;
  timestamp: string;
  userDisplayName: string;
  userId: string;
}

export interface ITransformContent {
  result: string;
}

export interface IHook {
  run(): Promise<void>;
}

export interface IMediaPlayerPlayRequest {
  sourceUrl: string;
  sourceType: "AUDIO" | "VIDEO" | "IMAGE" | "CO-BROWSE";
  sourceFileName: string;
}

export interface IDateRange {
  startAt: dayjs.Dayjs;
  endAt: dayjs.Dayjs;
}

export interface IMetricWidget extends IMetricConfig {
  id?: string;
  size: "sm" | "md" | "lg";
}

export interface IMetricConfig {
  name?: string;
  type: MetricTypeEnum;
  rangePreset: DateRangePresetEnum;
  range?: MetricRangeEnum;
  startYear?: number;
  endYear?: number;
  month?: number;
  week?: number;
  day?: number;
}

export interface IMetricDatum {
  year: number;
  month: number;
  week: number;
  day: number;
  hour: number;
  value: number;
}

export interface IHTMLOption {
  value: string;
  label: string;
}

export interface IMetricDashboard {
  name: string;
  metrics: IMetricWidget[];
}

export interface IStorageTestResult {
  passed: boolean;
  message: string;
  errorType?:
    | "BUCKET_NOT_FOUND"
    | "PERMISSION_DENIED"
    | "AUTHENTICATION_FAILED"
    | "RATE_LIMIT"
    | "REGION_MISMATCH"
    | "UNKNOWN";
}

export interface IStorageTestSummary {
  bucketTestResult: IStorageTestResult;
  deleteTestResult: IStorageTestResult;
  modifyTestResult: IStorageTestResult;
  readTestResult: IStorageTestResult;
  writeTestResult: IStorageTestResult;
  throughputTestResult: IStorageTestResult & {
    // only passed when succesful
    bytesTransferred?: number;
    durationMillis?: number;
    throughputMbps?: number;
  };
}

export interface IAuditLog {
  audit: {
    timeStamp: string;
    interactionID: string;
    organizationId: string;
    userId: string;
    roles: UserRoleEnum[];
    action: AuditActionEnum;
    outcome: AuditOutcomeEnum;
    application: AuditApplicationEnum;
    applicationId?: string;
    detail: string;
  };
}

export interface IFilterSelection<T> {
  checked: boolean;
  value: T;
}

export interface ISpeechToTextTranscriptChunk {
  userId: string;
  transcript: string;
  isFinal: boolean;
}

export interface ITranscriptStatus {
  id: string;
  state: TranscriptStateEnum;
  language: string;
  createdAt: Date;
}

export interface ITranscriptTranslateStatus extends ITranscriptStatus {
  updatedAt?: Date;
  transcript: string;
}

export interface ITranscriptTransformStatus {
  id: string;
  transcript: string;
  intent: TranscriptIntentType;
  state: TranscriptStateEnum;
  createdAt: Date;
  updatedAt?: Date;
}

export interface ITZonePickerEntity {
  abbr?: string;
  group?: string;
  nameValue: string;
  timeValue?: string;
  name?: string;
}

export interface ITicketAppointmentOptions {
  queueName?: string;
}

export interface ILocaleEntity {
  locale: string;
  language?: string;
  name: string;
  localisedName?: string;
}

export interface ISearchableItem {
  id: string;
  key: string;
  text: string;
  textNormalized?: string;
  onMatch: (matches: string[]) => void;
}

export interface IQueueBackground {
  assetId: string;
  queueId: string;
}

export interface IQueueAsset {
  type: AssetTypeEnum;
  url: string;
  queueId?: string;
  queueName?: string;
}

/** Maps an error page code (see ErrorPageCode) to a custom image URL. */
export interface IErrorPageImage {
  code: string;
  url: string;
}

export interface IGeolocationCoordinates {
  latitude: number;
  longitude: number;
  accuracy?: number;
}

export interface IMetadata<T> {
  key: string;
  value: string;
  type?: T;
}

export type WebhookCustomHeaderType = "DEFAULT" | "SECRET";

export interface ISelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export type VisionState =
  | "snapshotRequested"
  | "snapshotReady"
  | "snapshotError"
  | "analysisRequested"
  | "analysisReady"
  | "analysisError";

export interface IVisionSnapshotState {
  id: string;
  state: VisionState;
  result?: string;
  taskId?: string;
}

/**
 * An AI vision analysis as rtc keeps it on the snapshot it was made of. The
 * ai service writes it (not a call participant), so it is the record of an
 * analysis after the call and for agents who did not request it. Absent for
 * a snapshot that was never analysed, and on an rtc that does not know it.
 */
export interface ISnapshotAnalysis {
  state: "PENDING" | "COMPLETED" | "FAILED";
  /** The provider's text: untrusted, to be shown as text only. */
  result?: string;
  /** The ai recognition task, i.e. the id its MQTT events carry. */
  taskId?: string;
  error?: string;
  /** ISO timestamp of the last change. */
  updatedAt: string;
}

/**
 * State machine for a single deepfake check.
 * idle -> submitted [-> listening] -> recording -> uploading -> analysing
 *      -> verdict | error | timeout
 *
 * The steps before `analysing` all happen in this browser, and each is shown
 * to the agent as its own line in the badge's tooltip:
 *
 * `submitted`:  the check exists, the media is about to be captured.
 * `listening`:  a clip check holding its recording until the participant is
 *               heard speaking (the "start the clip on a voice" option).
 * `recording`:  capturing the still or recording the clip.
 * `uploading`:  the captured media is on its way to the ai service.
 * `analysing`:  Reality Defender has it; we are waiting for the verdict.
 *
 * A still-image check has no `listening` step and passes through `recording`
 * in an instant, so the agent normally sees `uploading` first.
 */
export type DeepfakeCheckState =
  | "idle"
  | "submitted"
  | "listening"
  | "recording"
  | "uploading"
  | "analysing"
  | "verdict"
  | "error"
  | "timeout";

/** The states of a check that has not settled yet. */
export const DEEPFAKE_PENDING_STATES: readonly DeepfakeCheckState[] = [
  "submitted",
  "listening",
  "recording",
  "uploading",
  "analysing"
];

export interface IDeepfakeCheckModelSummary {
  name: string;
  status: string;
  /** 0-100, higher = more likely synthetic. */
  score: number | null;
}

/** What RD's Context Aware model read from the frame (image scans only). */
export interface IDeepfakeContext {
  imageType?: string;
  /** A generator RD believes produced the frame. */
  potentialSource?: string;
  /** Public figures RD recognised. */
  recognizableIdentities?: string[];
  manipulationReasons?: string[];
  /** Caption of the frame; present only when the admin opted in. */
  description?: string;
}

/**
 * One media kind's outcome inside a check. The detail fields arrive only when
 * the application's deepfakeShow* options allow — the ai never sends a detail
 * the admin turned off, so the badge simply renders what is there.
 */
export interface IDeepfakeScanResult {
  kind: DeepfakeMediaKind;
  /** Present when this scan reached a verdict. */
  status?: string;
  score?: number | null;
  models?: IDeepfakeCheckModelSummary[];
  /** Why Reality Defender answered NOT_APPLICABLE (silence, music, two speakers...). */
  reasons?: { code: string; message: string }[];
  /** Settled, non-ensemble models sharing this scan's verdict. */
  agreement?: { agreeing: number; total: number };
  /** Seconds of media RD analysed. */
  analysedSeconds?: number;
  context?: IDeepfakeContext;
  /** Present instead of `status` when this scan could not run. */
  reason?: string;
}

export type DeepfakeMediaKind = "image" | "audio" | "video";
/** A whole check: what was submitted. */
export type DeepfakeCheckKind = "image" | "audio" | "image+audio" | "video";
/** Who started a check: the agent from the tile menu, or the automatic-check policy. */
export type DeepfakeTrigger = "agent" | "policy";

export interface IDeepfakeCheck {
  /** Local id of the check, made in this browser before anything is sent. */
  id: string;
  /**
   * The ai service's task id, which its events carry. Set once the request is
   * accepted. A check loaded from the ai's list (after a reload, or another
   * agent's) has it from the start, and uses it as its local `id` as well.
   */
  taskId?: string;
  /**
   * The user id of the agent whose request started the check, as the ai
   * records it. Known for checks loaded from the ai's list; a check another
   * agent started gets no MQTT events on this page and is followed by polling.
   */
  createdBy?: string;
  /** Endpoint of the participant that was checked. */
  targetEndpointId: string;
  /**
   * The participant's stream the media came from. A participant can show a
   * camera, a second camera and a screen at once, each with its own badge.
   * Absent only on checks made by an older client.
   */
  targetStreamId?: string;
  trigger?: DeepfakeTrigger;
  state: DeepfakeCheckState;
  mediaKind: DeepfakeCheckKind;
  /**
   * Headline verdict (the most severe scan), present in the "verdict" state
   * only. `results` carries every scan when more than one media was checked.
   */
  status?: string;
  /** 0-100, higher = more likely synthetic. */
  score?: number | null;
  models?: IDeepfakeCheckModelSummary[];
  results?: IDeepfakeScanResult[];
  /** Present in the "error"/"timeout" states only. Never rendered as a verdict. */
  reason?: string;
  createdAt: string;
  /**
   * The ai's `updatedAt` of the snapshot this check shows (its last event or
   * listed state), when the ai sends one: a settled check is replaced only
   * by a strictly newer settled snapshot (a late verdict of a re-check).
   */
  updatedAt?: string;
}
